import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "tailwindcss/tailwind.css";
import { BASE_URL } from "../../config";
import { ArrowLeftIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import { motion } from "framer-motion";

const RVU_VALUE_INPUT_PATTERN = /^\d{0,16}(?:\.\d{0,2})?$/;
const RVU_VALUE_PATTERN = /^(?:\d{1,16}(?:\.\d{1,2})?|\.\d{1,2})$/;

const EditRvu = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { rvuCode } = useParams();
  const isViewMode = location.pathname.startsWith("/rvu/view/");
  const [formData, setFormData] = useState({
    rvu_category: "",
    rvu_code: "",
    rvu_description: "",
    value: "",
  });
  const [loadingRvu, setLoadingRvu] = useState(true);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchRvu = async () => {
      setLoadingRvu(true);
      try {
        const response = await axios.get(`${BASE_URL}/rvu/${rvuCode}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
          },
        });
        const { rvu } = response.data.data;
        setFormData({
          rvu_category: rvu.rvu_category || "",
          rvu_code: rvu.rvu_code,
          rvu_description: rvu.rvu_description || "",
          value: rvu.value || "",
        });
      } catch (error) {
        toast.error("Failed to fetch RVU data.", { autoClose: 3000 });
        setTimeout(() => navigate("/rvu"), 2000);
      } finally {
        setLoadingRvu(false);
      }
    };

    fetchRvu();
  }, [rvuCode, navigate]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (name === "value" && !RVU_VALUE_INPUT_PATTERN.test(value)) return;

    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.rvu_code) newErrors.rvu_code = "RVU code is required.";
    if (!RVU_VALUE_PATTERN.test(formData.value) || Number(formData.value) <= 0) {
      newErrors.value =
        "RVU value must be greater than 0 with up to 16 whole-number digits and 2 decimal places.";
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isViewMode) return;

    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      Object.values(formErrors).forEach((error) => toast.error(error, { autoClose: 3000 }));
      return;
    }
    try {
      await axios.put(
        `${BASE_URL}/rvu/${rvuCode}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
          },
        }
      );
      toast.success("RVU code updated successfully.", { autoClose: 3000 });
      setTimeout(() => navigate("/rvu"), 2000);
    } catch (error) {
      if (error.response?.status === 422 && error.response?.data?.message) {
        if (typeof error.response.data.message === "string") {
          toast.error(error.response.data.message, { autoClose: 3000 });
        } else {
          Object.values(error.response.data.message).forEach((messages) => {
            const messageArray = Array.isArray(messages) ? messages : [messages];
            messageArray.forEach((message) => toast.error(message, { autoClose: 3000 }));
          });
        }
      } else {
        toast.error("Failed to update RVU code.", { autoClose: 3000 });
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-2 px-2 sm:px-6 lg:px-8">
      <ToastContainer position="top-right" autoClose={3000} />
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-3xl mx-auto"
      >
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-2xl font-bold text-gray-900">
            {isViewMode ? "View RVU Code" : "Edit RVU Code"}: {rvuCode}
          </h1>
          <div className="flex flex-wrap items-center gap-2 self-start sm:self-auto">
            {isViewMode && (
              <button
                type="button"
                onClick={() => navigate(`/rvu/edit/${rvuCode}`)}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-amber-500 px-4 py-2 text-sm font-medium text-white hover:bg-amber-600"
              >
                <PencilSquareIcon className="h-5 w-5" />
                <span>Edit</span>
              </button>
            )}
            <button
              type="button"
              onClick={() => navigate("/rvu")}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
            >
              <ArrowLeftIcon className="h-5 w-5" />
              <span>Back to RVU Codes</span>
            </button>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-md">
          {loadingRvu ? (
            <div className="text-center text-sm text-gray-500">Loading...</div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700">RVU Category</label>
                  <input
                    type="text"
                    name="rvu_category"
                    value={formData.rvu_category}
                    onChange={handleFormChange}
                    disabled={isViewMode}
                    className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-600"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">RVU Code *</label>
                  <input
                    type="text"
                    name="rvu_code"
                    value={formData.rvu_code}
                    disabled
                    className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 bg-gray-100 text-sm"
                  />
                </div>
                {/* <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">RVU Description</label>
                  <input
                    type="text"
                    name="rvu_description"
                    value={formData.rvu_description}
                    onChange={handleFormChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                  />
                </div> */}

                <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700">RVU Description</label>
                    <textarea
                        name="rvu_description"
                        value={formData.rvu_description}
                        onChange={handleFormChange}
                        disabled={isViewMode}
                        className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-600"
                        rows={4} // Adjust the number of rows as needed
                    />
                </div>


                <div>
                  <label className="block text-sm font-medium text-gray-700">Value *</label>
                  <input
                    type="text"
                    name="value"
                    value={formData.value}
                    onChange={handleFormChange}
                    disabled={isViewMode}
                    className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-600"
                    inputMode="decimal"
                    maxLength={19}
                  />
                  {errors.value && <p className="mt-1 text-sm text-red-600">{errors.value}</p>}
                </div>
              </div>
              {!isViewMode && (
                <div className="mt-6 flex justify-end">
                  <button
                    type="submit"
                    className="bg-green-600 text-white px-4 py-2 rounded-md text-sm hover:bg-green-700"
                  >
                    Save
                  </button>
                </div>
              )}
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default EditRvu;
